/* Grievance Dashboard — the numbers HR and an auditor both ask for.

   Scoped server-side to whatever the caller may read, so this renders for an
   org admin (whole company), a category handler (their queue) and a plain
   employee (their own record) without a branch here.

   This screen is the reference the shared dashboard kit was generalised from,
   so it is built entirely out of it: the tiles, bars, sections and shortlist
   table that used to live here are now components/dashboard. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, BarChart3, CheckCircle2, Clock, Gauge, Inbox, RotateCcw, Timer,
} from "lucide-react";

import {
    DashboardSection, DashboardShell, DashboardTable, DistributionBars, SectionGrid,
    StatTile, TileGrid,
} from "../../components/dashboard";
import { Button } from "../../components/ui/button";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { GRIEVANCE_DASHBOARD } from "../../constants/api";
import { GRIEVANCE_STATUS_LABELS } from "../../constants/grievance";
import { fmtDate, PriorityBadge, StatusBadge } from "./GrievancePieces";

const PERIODS = [
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
    { value: "365", label: "1 year" },
];

const PRIORITY_BAR = {
    low: "bg-gray-400",
    medium: "bg-blue-500",
    high: "bg-orange-500",
    critical: "bg-red-500",
};

const GrievanceDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [days, setDays] = useState("90");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(GRIEVANCE_DASHBOARD, { days });
            if (res?.status === "success") setData(res.data);
            else ToastMsg("error", res?.message || "Could not load the dashboard");
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const tiles = data?.tiles || {};
    const total = tiles.total || 0;

    const statusRows = (data?.by_status || []).map((r) => ({
        key: r.status,
        label: r.status_label || GRIEVANCE_STATUS_LABELS[r.status] || r.status,
        count: r.count,
        status: r.status,
    }));
    const categoryRows = (data?.by_category || []).map((r) => ({
        key: String(r.category_id ?? "none"),
        label: r.category_name,
        count: r.count,
        isSensitive: r.is_sensitive,
    }));
    const priorityRows = (data?.by_priority || []).map((r) => ({
        key: r.priority,
        label: (r.priority || "").replace(/^./, (c) => c.toUpperCase()),
        count: r.count,
        priority: r.priority,
    }));

    const attentionColumns = [
        {
            key: "code",
            header: "Grievance",
            render: (row) => (
                <>
                    <span className="font-semibold text-sidebar-primary">{row.code}</span>
                    <span className="ml-2 text-muted-foreground">
                        {(row.subject || "").length > 48
                            ? `${row.subject.slice(0, 48)}…`
                            : (row.subject || "—")}
                    </span>
                </>
            ),
        },
        { key: "category_name", header: "Category" },
        { key: "priority", header: "Priority", render: (row) => <PriorityBadge priority={row.priority} /> },
        {
            key: "status",
            header: "Status",
            render: (row) => <StatusBadge status={row.status} resolutionType={row.resolution_type} />,
        },
        { key: "assigned_to_name", header: "Handler", render: (row) => row.assigned_to_name || "Unassigned" },
        {
            key: "resolution_due_at",
            header: "Due",
            className: "py-2 text-xs text-red-600 dark:text-red-400",
            render: (row) => fmtDate(row.resolution_due_at),
        },
    ];

    return (
        <DashboardShell
            Icon={BarChart3}
            title="Grievance Dashboard"
            subtitle="Volume, ageing and SLA compliance across the grievances you can see"
            loading={loading}
            hasData={!!data}
            toolbar={<SegmentSwitch options={PERIODS} value={days} onChange={setDays} />}
            actions={
                <Button variant="outline" size="sm" onClick={() => navigate("/grievance/inbox")}>
                    <Inbox className="h-4 w-4" /> Open inbox
                </Button>
            }
        >
            <TileGrid>
                <StatTile Icon={BarChart3} label="Total" value={tiles.total} />
                <StatTile Icon={Clock} label="Open now" value={tiles.open} />
                <StatTile
                    Icon={AlertTriangle}
                    label="Overdue"
                    value={tiles.breached}
                    tone={tiles.breached ? "danger" : "default"}
                    hint={tiles.breached ? "Past acknowledgement or resolution SLA" : "Nothing past SLA"}
                />
                <StatTile
                    Icon={CheckCircle2}
                    label="Resolved"
                    value={tiles.resolved_this_period}
                    tone="success"
                    hint={`In the last ${data?.period_days ?? days} days`}
                />
                <StatTile Icon={Timer} label="Avg resolution" value={tiles.avg_resolution_days} suffix="days" />
                <StatTile
                    Icon={Gauge}
                    label="SLA compliance"
                    value={tiles.sla_compliance_percent}
                    suffix="%"
                    tone={
                        tiles.sla_compliance_percent === null || tiles.sla_compliance_percent === undefined
                            ? "default"
                            : tiles.sla_compliance_percent >= 90 ? "success" : "danger"
                    }
                    hint="Resolved on or before the due date"
                />
                <StatTile
                    Icon={RotateCcw}
                    label="Reopen rate"
                    value={tiles.reopen_rate_percent}
                    suffix="%"
                    hint="High means resolutions are closing too early"
                />
                <StatTile Icon={Inbox} label="My grievances open" value={tiles.my_open} />
            </TileGrid>

            <SectionGrid>
                <DashboardSection title="By status">
                    <DistributionBars
                        rows={statusRows}
                        total={total}
                        emptyText="No grievances yet."
                        colorFor={(row) =>
                            ["escalated", "reopened"].includes(row.status)
                                ? "bg-rose-500"
                                : ["resolved", "closed"].includes(row.status)
                                    ? "bg-emerald-500"
                                    : "bg-sidebar-primary"
                        }
                    />
                </DashboardSection>

                <DashboardSection title="By category">
                    <DistributionBars
                        rows={categoryRows}
                        total={total}
                        emptyText="No grievances yet."
                        colorFor={(row) => (row.isSensitive ? "bg-rose-500" : "bg-indigo-500")}
                    />
                </DashboardSection>
            </SectionGrid>

            <DashboardSection title="By priority">
                <DistributionBars
                    rows={priorityRows}
                    total={total}
                    emptyText="No grievances yet."
                    colorFor={(row) => PRIORITY_BAR[row.priority] || "bg-blue-500"}
                />
            </DashboardSection>

            <DashboardSection title="Needs attention">
                <DashboardTable
                    columns={attentionColumns}
                    rows={data?.attention || []}
                    onRowClick={(row) => navigate(`/grievance/inbox?id=${row.id}`)}
                    empty="Nothing is past its SLA. Good place to be."
                />
            </DashboardSection>
        </DashboardShell>
    );
};

export default GrievanceDashboard;
